import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from '@/components/icons';
import { PLATFORM_ROLES, ROLE_LABELS, type PlatformRole } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import type { AdminUser, User } from '@/lib/types';
import { Badge, Card, ErrorNotice, Input, PageHeader, PageLoader, Select, Toggle, useToast } from '@/components/ui';

export function AdminUsersPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers });
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<'ALL' | PlatformRole>('ALL');

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { platformRole?: PlatformRole; status?: User['status'] } }) => api.adminUpdateUser(id, patch),
    onSuccess: () => {
      toast.success('User updated');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = (u: AdminUser) =>
      !needle ||
      u.name.toLowerCase().includes(needle) ||
      u.email.toLowerCase().includes(needle) ||
      u.memberships.some((m) => m.orgName.toLowerCase().includes(needle));
    return (users.data ?? []).filter((u) => (roleFilter === 'ALL' || u.platformRole === roleFilter) && matches(u));
  }, [users.data, q, roleFilter]);

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Users"
        description="Everyone with a 1QA account, the organizations they belong to and their role there. Project Admins own one organization each."
      />
      {users.isPending ? (
        <PageLoader />
      ) : users.isError ? (
        <ErrorNotice error={users.error} onRetry={() => users.refetch()} />
      ) : (
        <Card>
          <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute top-1/2 left-4 size-3.5 -translate-y-1/2 text-fg-muted" />
                <Input
                  aria-label="Search users"
                  placeholder="Search by name, email or organization…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select
                aria-label="Filter by platform role"
                wrapperClassName="w-48"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as 'ALL' | PlatformRole)}
                options={[{ value: 'ALL', label: 'All roles' }, ...PLATFORM_ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))]}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-wide text-slate-500 uppercase">
                  <th className="px-5 py-3 font-medium">User</th>
                  <th className="px-5 py-3 font-medium">Platform role</th>
                  <th className="px-5 py-3 font-medium">Organizations</th>
                  <th className="px-5 py-3 font-medium">Active</th>
                  <th className="px-5 py-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((u) => {
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30">
                      <td className="px-5 py-3">
                        <div className="font-medium">
                          {u.name} {isMe && <span className="text-slate-400">(you)</span>}
                        </div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td className="px-5 py-3">
                        {isMe ? (
                          <Badge tone="brand">{ROLE_LABELS[u.platformRole]}</Badge>
                        ) : (
                          <Select
                            aria-label={`Platform role for ${u.name}`}
                            wrapperClassName="w-44"
                            value={u.platformRole}
                            onChange={(e) => update.mutate({ id: u.id, patch: { platformRole: e.target.value as PlatformRole } })}
                            options={PLATFORM_ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
                          />
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {u.memberships.length === 0 ? (
                          <span className="text-xs text-slate-400">
                            {u.platformRole === 'PROJECT_ADMIN' ? 'No organization created yet' : '—'}
                          </span>
                        ) : (
                          <div className="flex max-w-sm flex-wrap gap-1.5">
                            {u.memberships.map((m) => (
                              <Badge key={m.orgId} tone={m.role === 'PROJECT_ADMIN' ? 'brand' : 'neutral'}>
                                {m.orgName} · {ROLE_LABELS[m.role]}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {u.projectsAssigned > 0 && (
                          <div className="mt-1 text-xs text-slate-500">
                            Assigned to {u.projectsAssigned} project{u.projectsAssigned === 1 ? '' : 's'}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <Toggle
                          label={`${u.status === 'ACTIVE' ? 'Disable' : 'Enable'} ${u.name}`}
                          checked={u.status === 'ACTIVE'}
                          disabled={isMe || update.isPending}
                          onChange={(on) => update.mutate({ id: u.id, patch: { status: on ? 'ACTIVE' : 'DISABLED' } })}
                        />
                      </td>
                      <td className="px-5 py-3 text-slate-500">{timeAgo(u.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <p className="px-5 py-10 text-center text-sm text-slate-500">No users match your search.</p>}
          </div>
        </Card>
      )}
    </div>
  );
}
